import { Box, Typography } from "@mui/material";
import { useState, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  Button,
  FileUpload,
  Input,
  PhoneNumber,
  Select,
} from "src/components/common/FormComponents";
import Password from "src/components/common/FormComponents/Password";
import { OTP, SuccessModal } from "src/components/common/modal";
import Snackbar from "src/components/common/Snackbar";
import { OtpType } from "src/constants/common";
import errorMessage from "src/constants/errorMessage";
import { path } from "src/constants/path";
import { getStyles } from "src/styles/theme";
import { SnackbarType } from "src/types/common";
import { registerGCA } from "src/utils/api/gca";
import { getStates } from "src/utils/api/roles";
import regex from "src/utils/regex";
import { transformFormDataToRegistrationPayload } from "src/utils/transformers/gcaProfileTransformer";
import defaultStyles, { StylesClasses } from "./styles";

const styles = getStyles<StylesClasses>(defaultStyles);

export type GCARegistrationFormData = {
  // GCA Type
  gcaType: string;

  // Government Institutions - Basic Details
  nameOfInstitution?: string;
  typeOfInstitution?: string;
  ministry?: string;
  organizationPAN?: string;
  organizationGST?: string;
  institutionalEmail?: string;
  officialContactNumber?: string;
  website?: string;

  // Government Institutions - Registered Address
  address?: string;
  state?: string;
  district?: string;
  pincode?: string;

  // Government Institutions - Authorized Representative
  repFullName?: string;
  repDesignation?: string;
  repEmail?: string;
  repMobile?: {
    countryCode: string;
    number: string;
  };
  repEmployeeID?: string;

  // PSU - Organization Details
  nameOfPSU?: string;
  ownershipType?: string;
  parentCompanyDetails?: string;
  subsidiariesDetails?: string[];
  cinNumber?: string;
  gstNumber?: string;
  primaryEmail?: string;
  primaryContactNumber?: string;
  officeAddress?: string;
  officeState?: string;
  officeDistrict?: string;
  officePincode?: string;

  // NGO - Details
  nameOfNGO?: string;
  registrationType?: string;
  ngoRegistrationNumber?: string;
  ngoGSTNumber?: string;
  ngoPANNumber?: string;
  status12A80G?: string;
  ngoDarpanID?: string;
  ngoEmail?: string;
  ngoContactNumber?: string;
  ngoOfficeAddress?: string;
  ngoOfficeState?: string;
  ngoOfficeDistrict?: string;
  ngoOfficePincode?: string;
  operationActiveStates?: string;
  natureOfWork?: string;
  turnover?: string;

  // Private Company - Business Information
  companyName?: string;
  companyPAN?: string;
  companyGSTNumber?: string;
  parentCompanyDetailsPC?: string;
  subsidiariesDetailsPC?: string[];
  natureOfBusinessPC?: string;
  statesOfOperationPC?: string;
  incorporationDate?: string;
  companyEmail?: string;
  companyMobileNumber?: string;
  pcOfficeAddress?: string;
  pcOfficeState?: string;
  pcOfficeDistrict?: string;
  pcOfficePincode?: string;

  // Public Limited Company - Company Details
  registeredCompanyName?: string;
  plcCINNumber?: string;
  plcPANNumber?: string;
  plcGSTNumber?: string;
  sebiListingStatus?: string;
  parentCompanyDetailsPLC?: string;
  subsidiariesDetailsPLC?: string[];
  corporateOfficeEmail?: string;
  contactNumberPLC?: string;
  registeredOffice?: string;
  corporateOffice?: string;
  plcOfficeAddress?: string;
  plcOfficeState?: string;
  plcOfficeDistrict?: string;
  plcOfficePincode?: string;

  // Philanthropies / Foundations - Organization Details
  foundationName?: string;
  legalStatusFoundation?: string;
  foundationRegistrationNumber?: string;
  foundationPANNumber?: string;
  fundingSource?: string;
  foundationEmail?: string;
  foundationMobile?: {
    countryCode: string;
    number: string;
  };
  foundationOfficeAddress?: string;
  foundationOfficeState?: string;
  foundationOfficeDistrict?: string;
  foundationOfficePincode?: string;

  // Philanthropies / Foundations - CSR and ESG
  csrEsgDetails?: string;

  // Individuals - Personal Details
  individualFullName?: string;
  individualDateOfBirth?: string;
  individualGender?: string;
  individualOccupation?: string;
  individualAadhaarNumber?: string;
  individualPANNumber?: string;
  individualEmail?: string;
  individualMobile?: {
    countryCode: string;
    number: string;
  };
  individualAddress?: string;
  individualState?: string;
  individualDistrict?: string;
  individualPincode?: string;
  individualOccupationDetails?: string;

  // Registered Group of Individuals - Group Details
  groupName?: string;
  groupType?: string;
  groupTypeOther?: string;
  groupEmail?: string;
  groupContactNumber?: {
    countryCode: string;
    number: string;
  };
  groupRegistrationNumber?: string;
  groupOfficeAddress?: string;
  groupOfficeState?: string;
  groupOfficeDistrict?: string;
  groupOfficePincode?: string;

  middleName?: string;
  lastName?: string;
  gender?: string;
  dateOfBirth?: string;
  aadhaarNumber?: string;
  panNumber?: string;

  // Contact Details
  email?: string;
  phone?: {
    countryCode: string;
    number: string;
  };
  alternatePhone?: {
    countryCode: string;
    number: string;
  };

  // Address Details (Generic)
  houseNumber?: string;
  streetName?: string;
  locality?: string;
  city?: string;

  // Professional Details
  qualification?: string;
  profession?: string;
  organizationName?: string;
  registrationNumber?: string;
  designation?: string;
  yearsOfExperience?: string;

  // Areas of Expertise
  areasOfExpertise?: string;
  specialization?: string;

  // Document Uploads - Government Institutions
  uploadPAN?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadGST?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadCertificate?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadRepIDProof?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadAuthorizationLetter?: {
    type: string;
    key: string;
    file: string;
  }[];

  // Document Uploads - PSU
  uploadCertificateOfIncorporation?: {
    type: string;
    key: string;
    file: string;
  }[];

  // Document Uploads - NGO
  uploadNGORegistrationCertificate?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadNGOGST?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadNGOPAN?: {
    type: string;
    key: string;
    file: string;
  }[];
  upload12A80GCertificates?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadNGODarpanCertificate?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadAuditedFinancialStatements?: {
    type: string;
    key: string;
    file: string;
  }[];

  // Document Uploads - Private Company
  uploadCertificateOfIncorporationPC?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadPANPC?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadGSTCertificatePC?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadAuditedFinancialStatementsPC?: {
    type: string;
    key: string;
    file: string;
  }[];

  // Public Limited Company - Document Uploads
  uploadIncorporationDocumentsPLC?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadPANPLC?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadGSTPLC?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadAuditedFinancialStatementsPLC?: {
    type: string;
    key: string;
    file: string;
  }[];

  // Philanthropies / Foundations - Document Uploads
  uploadRegistrationCertificateFoundation?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadPANFoundation?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadAuditedFinancialStatementsFoundation?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadCSREsgDocument?: {
    type: string;
    key: string;
    file: string;
  }[];

  // Individuals - Document Uploads
  uploadPassportPhoto?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadIndividualAadhaar?: {
    type: string;
    key: string;
    file: string;
  }[];
  uploadIndividualPAN?: {
    type: string;
    key: string;
    file: string;
  }[];

  // Registered Group of Individuals - Document Uploads
  uploadGroupRegistrationCertificate?: {
    type: string;
    key: string;
    file: string;
  }[];

  profilePhoto?: {
    type: string;
    key: string;
    file: string;
  }[];
  aadhaarDocument?: {
    type: string;
    key: string;
    file: string;
  }[];
  panDocument?: {
    type: string;
    key: string;
    file: string;
  }[];
  qualificationCertificate?: {
    type: string;
    key: string;
    file: string;
  }[];
  experienceCertificate?: {
    type: string;
    key: string;
    file: string;
  }[];

  // Account Security
  password: string;
  confirmPassword: string;

  // Verification
  verifyEmail?: boolean;
  verifyPhone?: boolean;
  verifyRepEmail?: boolean;
  verifyRepMobile?: boolean;
  verifyInstitutionalEmail?: boolean;
  verifyPrimaryEmail?: boolean;
  verifyNGOEmail?: boolean;
  emailOtp?: string;
  phoneOtp?: string;
  repEmailOtp?: string;
  repMobileOtp?: string;
  institutionalEmailOtp?: string;
  primaryEmailOtp?: string;
  ngoEmailOtp?: string;
  verifyCompanyEmail?: boolean;
  companyEmailOtp?: string;
  verifyCompanyMobile?: boolean;
  companyMobileOtp?: string;
  verifyCorporateOfficeEmail?: boolean;
  corporateOfficeEmailOtp?: string;
  verifyFoundationEmail?: boolean;
  foundationEmailOtp?: string;
  verifyFoundationMobile?: boolean;
  foundationMobileOtp?: string;
  verifyIndividualEmail?: boolean;
  individualEmailOtp?: string;
  verifyIndividualMobile?: boolean;
  individualMobileOtp?: string;
  verifyGroupEmail?: boolean;
  groupEmailOtp?: string;
  verifyGroupContact?: boolean;
  groupContactOtp?: string;

  // Terms and Conditions
  acceptTerms?: boolean;
};

const gcaTypeOptions = [
  { label: "Government Institutions", value: "GOVERNMENT_INSTITUTIONS" },
  { label: "Public Sector Undertakings (PSUs)", value: "PSU" },
  { label: "Non-Governmental Organizations (NGOs)", value: "NGO" },
  { label: "Private Companies", value: "PRIVATE_COMPANY" },
  { label: "Public Limited Company", value: "PUBLIC_LIMITED_COMPANY" },
  { label: "Philanthropies / Foundations", value: "PHILANTHROPY" },
  { label: "Individuals", value: "INDIVIDUAL" },
  { label: "Registered Group of Individuals", value: "REGISTERED_GROUP" },
];

const institutionTypeOptions = [
  { label: "Central Govt.", value: "CENTRAL_GOVT" },
  { label: "State Govt.", value: "STATE_GOVT" },
  { label: "Autonomous Body", value: "AUTONOMOUS_BODY" },
  { label: "Statutory Body", value: "STATUTORY_BODY" },
];

const ownershipTypeOptions = [
  { label: "Central PSU", value: "CENTRAL_PSU" },
  { label: "State PSU", value: "STATE_PSU" },
];

const registrationTypeOptions = [
  { label: "Society", value: "SOCIETY" },
  { label: "Trust", value: "TRUST" },
  { label: "Section 8 Company", value: "SECTION_8_COMPANY" },
];

const natureOfWorkOptions = [
  { label: "Environment", value: "ENVIRONMENT" },
  { label: "Social", value: "SOCIAL" },
  { label: "Plantation", value: "PLANTATION" },
  { label: "Livelihood", value: "LIVELIHOOD" },
  { label: "Others (specify)", value: "OTHERS" },
];

const sebiListingStatusOptions = [
  { label: "NSE", value: "NSE" },
  { label: "BSE", value: "BSE" },
  { label: "Unlisted Public Ltd.", value: "UNLISTED_PUBLIC_LTD" },
];

const legalStatusFoundationOptions = [
  { label: "Society", value: "SOCIETY" },
  { label: "Trust", value: "TRUST" },
  { label: "Non-profit Foundation", value: "NON_PROFIT_FOUNDATION" },
];

const occupationDetailsOptions = [
  { label: "Government", value: "GOVERNMENT" },
  { label: "Private", value: "PRIVATE" },
  { label: "Student", value: "STUDENT" },
  { label: "Self-employed", value: "SELF_EMPLOYED" },
  { label: "Other", value: "OTHER" },
];

const genderOptions = [
  { label: "Male", value: "MALE" },
  { label: "Female", value: "FEMALE" },
  { label: "Others", value: "OTHERS" },
];

const groupTypeOptions = [
  { label: "Self-Help Group (SHGs)", value: "SHG" },
  { label: "Farmer Producer Organizations (FPOs)", value: "FPO" },
  { label: "Village Committee", value: "VILLAGE_COMMITTEE" },
  { label: "Others", value: "OTHER" },
];

const GCARegistration = () => {
  const navigate = useNavigate();

  const [showSnackbar, setShowSnackbar] = useState<SnackbarType>({
    severity: "success",
    message: "",
  });

  const [stateOptions, setStateOptions] = useState<any[]>([]);

  // Fetch states from API
  useQuery(["getStates"], () => getStates(), {
    onSuccess: (data) => {
      setStateOptions(data.data || []);
    },
    refetchOnMount: true,
  });

  const [openModal, setOpenModal] = useState({
    open: false,
    heading: "",
    subheading: "",
    description: "",
    btnText: "",
    type: "",
  });

  const [loading, setLoading] = useState<boolean>(false);

  const { password: passwordRegex } = regex;

  const {
    control,
    formState,
    handleSubmit,
    getValues,
    setValue,
    watch,
    setError,
    clearErrors,
    trigger,
  } = useForm<GCARegistrationFormData>({
    mode: "onTouched",
    defaultValues: {
      gcaType: "",
      password: "",
      confirmPassword: "",
      acceptTerms: false,
    },
  });

  const { errors, isValid } = formState;

  // Get the current state value
  const currentStateValue = watch("state");

  // Compute district options based on selected state
  const districtOptions = useMemo(() => {
    if (!currentStateValue) {
      return [];
    }

    const selectedState = stateOptions.find(
      (s: any) => s.id === currentStateValue
    );

    const districts = selectedState?.districts || [];

    return districts;
  }, [currentStateValue, stateOptions]);

  // Transform state options for form (label/value format)
  const formattedStateOptions = useMemo(() => {
    if (!stateOptions || stateOptions.length === 0) {
      return [];
    }
    return stateOptions.map((state: any) => ({
      label: state.name || "",
      value: state.id || "",
      isActive: true,
    }));
  }, [stateOptions]);

  // Transform district options for form (label/value format)
  const formattedDistrictOptions = useMemo(() => {
    if (!districtOptions || districtOptions.length === 0) {
      return [];
    }
    const formatted = districtOptions.map((district: any) => ({
      label: district.name || "",
      value: district.id || "",
      isActive: true,
    }));
    return formatted;
  }, [districtOptions]);

  // Reset district when state changes
  const selectedState = watch("state");
  useEffect(() => {
    if (selectedState) {
      clearErrors("district");
    } else {
      setValue("district", "");
    }
  }, [selectedState, setValue, clearErrors]);

  // Create district options for each GCA type
  const officeStateValue = watch("officeState");
  const officeDistrictOptions = useMemo(() => {
    if (!officeStateValue) return [];
    const selectedState = stateOptions.find(
      (s: any) => s.id === officeStateValue
    );
    if (!selectedState?.districts) return [];
    return selectedState.districts.map((district: any) => ({
      label: district.name || "",
      value: district.id || "",
      isActive: true,
    }));
  }, [officeStateValue, stateOptions]);

  const ngoOfficeStateValue = watch("ngoOfficeState");
  const ngoOfficeDistrictOptions = useMemo(() => {
    if (!ngoOfficeStateValue) return [];
    const selectedState = stateOptions.find(
      (s: any) => s.id === ngoOfficeStateValue
    );
    if (!selectedState?.districts) return [];
    return selectedState.districts.map((district: any) => ({
      label: district.name || "",
      value: district.id || "",
      isActive: true,
    }));
  }, [ngoOfficeStateValue, stateOptions]);

  const pcOfficeStateValue = watch("pcOfficeState");
  const pcOfficeDistrictOptions = useMemo(() => {
    if (!pcOfficeStateValue) return [];
    const selectedState = stateOptions.find(
      (s: any) => s.id === pcOfficeStateValue
    );
    if (!selectedState?.districts) return [];
    return selectedState.districts.map((district: any) => ({
      label: district.name || "",
      value: district.id || "",
      isActive: true,
    }));
  }, [pcOfficeStateValue, stateOptions]);

  const plcOfficeStateValue = watch("plcOfficeState");
  const plcOfficeDistrictOptions = useMemo(() => {
    if (!plcOfficeStateValue) return [];
    const selectedState = stateOptions.find(
      (s: any) => s.id === plcOfficeStateValue
    );
    if (!selectedState?.districts) return [];
    return selectedState.districts.map((district: any) => ({
      label: district.name || "",
      value: district.id || "",
      isActive: true,
    }));
  }, [plcOfficeStateValue, stateOptions]);

  const foundationOfficeStateValue = watch("foundationOfficeState");
  const foundationOfficeDistrictOptions = useMemo(() => {
    if (!foundationOfficeStateValue) return [];
    const selectedState = stateOptions.find(
      (s: any) => s.id === foundationOfficeStateValue
    );
    if (!selectedState?.districts) return [];
    return selectedState.districts.map((district: any) => ({
      label: district.name || "",
      value: district.id || "",
      isActive: true,
    }));
  }, [foundationOfficeStateValue, stateOptions]);

  const individualStateValue = watch("individualState");
  const individualDistrictOptions = useMemo(() => {
    if (!individualStateValue) return [];
    const selectedState = stateOptions.find(
      (s: any) => s.id === individualStateValue
    );
    if (!selectedState?.districts) return [];
    return selectedState.districts.map((district: any) => ({
      label: district.name || "",
      value: district.id || "",
      isActive: true,
    }));
  }, [individualStateValue, stateOptions]);

  const groupOfficeStateValue = watch("groupOfficeState");
  const groupOfficeDistrictOptions = useMemo(() => {
    if (!groupOfficeStateValue) return [];
    const selectedState = stateOptions.find(
      (s: any) => s.id === groupOfficeStateValue
    );
    if (!selectedState?.districts) return [];
    return selectedState.districts.map((district: any) => ({
      label: district.name || "",
      value: district.id || "",
      isActive: true,
    }));
  }, [groupOfficeStateValue, stateOptions]);

  const registerMutation = useMutation(
    "registerGCA",
    (data: GCARegistrationFormData) => {
      // Transform form data to match GCA Profile table structure
      const transformedPayload = transformFormDataToRegistrationPayload(data);
      return registerGCA(transformedPayload);
    },
    {
      onSuccess: (res) => {
        setLoading(false);
        setOpenModal({
          open: true,
          heading: "Registration Successful",
          subheading: "",
          description:
            "Your GCA registration has been submitted successfully. You will receive a confirmation email shortly.",
          btnText: "Go to Login",
          type: "success",
        });
      },
      onError: (err: any) => {
        setLoading(false);
        setShowSnackbar({
          severity: "error",
          message: err?.response?.data?.message || "Registration failed",
        });
      },
      retry: 1,
    }
  );

  const handleFormSubmit = (data: GCARegistrationFormData) => {
    if (data.password !== data.confirmPassword) {
      setShowSnackbar({
        severity: "error",
        message: "Passwords do not match",
      });
      return;
    }

    // Email OTP verification removed for all entity types including INDIVIDUAL and REGISTERED_GROUP

    if (!data.acceptTerms) {
      setShowSnackbar({
        severity: "error",
        message: "Please accept terms and conditions",
      });
      return;
    }

    setLoading(true);
    registerMutation.mutate(data);
  };

  const handleModalClose = () => {
    setOpenModal({ ...openModal, open: false });
    navigate(path.login);
  };

  return (
    <>
      <Box {...styles("container")}>
        <Box {...styles("wrapper")}>
          <Box {...styles("header")}>
            <Typography {...styles("title")}>
              Green Credit Applicants (Entities) Registration Forms
            </Typography>
            <Typography {...styles("subtitle")}>
              Please fill in all the required information to complete your
              registration
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
            {/* GCA Type Selection */}
            <Box {...styles("section")}>
              <Typography {...styles("sectionTitle")}>
                Select GCA Type
              </Typography>

              <Box {...styles("row")}>
                <Box {...styles("input")}>
                  <Select
                    name="gcaType"
                    label="GCA Type"
                    control={control}
                    errors={errors}
                    height="52px"
                    rules={{ required: errorMessage.required }}
                    options={gcaTypeOptions}
                    placeholder="Select your GCA type"
                  />
                </Box>
              </Box>

              <Box {...styles("fieldHelperText")}>
                Please select the type that best describes your entity. Form
                fields will be shown based on your selection.
              </Box>
            </Box>

            {/* Government Institutions - Basic Details */}
            {watch("gcaType") === "GOVERNMENT_INSTITUTIONS" && (
              <>
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    Basic Details
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="nameOfInstitution"
                        label="Name of Institution"
                        placeholder="Enter name of institution"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Select
                        name="typeOfInstitution"
                        label="Type of Institution"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={institutionTypeOptions}
                        placeholder="Select type of institution"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="ministry"
                        label="Ministry"
                        placeholder="Enter ministry name"
                        control={control}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="organizationPAN"
                        label="Organization PAN No."
                        placeholder="Enter PAN number"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                            message: "Please enter a valid PAN number",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadPAN"
                        label="Upload PAN"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="organizationGST"
                        label="Organization GST No."
                        placeholder="Enter GST number"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadGST"
                        label="Upload GST document"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadCertificate"
                        label="Upload Certificate of Registration/Establishment Act"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "withHelperText"])}>
                      <Input
                        name="institutionalEmail"
                        label="Institutional Email ID"
                        placeholder="Enter institutional email"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Please enter a valid email address",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="officialContactNumber"
                        label="Official Contact Number"
                        placeholder="Enter official contact number"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^\d{10}$/,
                            message:
                              "Please enter a valid 10-digit phone number",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="website"
                        label="Website"
                        placeholder="Enter website URL"
                        control={control}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Typography {...styles("subsectionTitle")}>
                    Registered Address*
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="address"
                        label="Address"
                        placeholder="Enter complete address"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        multiline
                        rows={2}
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Select
                        name="state"
                        label="State"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={formattedStateOptions}
                        placeholder="Select state/UT"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Select
                        name="district"
                        label="District"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        options={formattedDistrictOptions}
                        placeholder="Select district"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="pincode"
                        label="Pin code"
                        placeholder="Enter 6-digit pin code"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^\d{6}$/,
                            message: "Please enter a valid 6-digit pin code",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>
                </Box>

                {/* B. Authorized Representative Details */}
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    Authorized Representative Details
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="repFullName"
                        label="Full Name"
                        placeholder="Enter full name"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="repDesignation"
                        label="Designation"
                        placeholder="Enter designation"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "withHelperText"])}>
                      <Input
                        name="repEmail"
                        label="Email ID"
                        placeholder="Enter email address"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Please enter a valid email address",
                          },
                        }}
                        errors={errors}
                        height="52px"
                        disabled={!!watch("verifyRepEmail")}
                      />

                      <OTP
                        name="repEmailOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        label="Verify Email"
                        isVerified={!!watch("verifyRepEmail")}
                        type={{
                          type: OtpType.EMAIL,
                          email: watch("repEmail"),
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("repEmailOtp", otp);
                          setValue("verifyRepEmail", true);
                        }}
                        heading="Verify representative email"
                        subHeading="You might have received an OTP on the representative's email. Please enter the same, If not click on resend OTP."
                      />
                    </Box>

                    <Box {...styles(["input", "withHelperText"])}>
                      <PhoneNumber
                        control={control}
                        errors={errors}
                        name="repMobile"
                        label="Mobile Number"
                        disabled={!!watch("verifyRepMobile")}
                      />

                      <OTP
                        name="repMobileOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        isVerified={!!watch("verifyRepMobile")}
                        type={{
                          type: OtpType.MOBILE,
                          phoneNumber: `${watch("repMobile.number")}`,
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("repMobileOtp", otp);
                          setValue("verifyRepMobile", true);
                        }}
                        heading="Verify representative mobile"
                        subHeading="You might have received an OTP on the representative's mobile. Please enter the same, If not click on resend OTP."
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="repEmployeeID"
                        label="Employee ID Number"
                        placeholder="Enter employee ID"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadRepIDProof"
                        label="Upload Authorized representative ID Proof"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadAuthorizationLetter"
                        label="Upload Authorization Letter"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            {/* PSU - Organization Details */}
            {watch("gcaType") === "PSU" && (
              <>
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    Organization Details
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="nameOfPSU"
                        label="Name of PSU"
                        placeholder="Enter name of PSU"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Select
                        name="ownershipType"
                        label="Ownership Type"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={ownershipTypeOptions}
                        placeholder="Select ownership type"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="parentCompanyDetails"
                        label="Parent Company details (if any)"
                        placeholder="Enter parent company details"
                        control={control}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Typography {...styles("subsectionTitle")}>
                        Subsidiaries details
                      </Typography>
                      <Box {...styles("fieldHelperText")}>
                        Add multiple subsidiaries if applicable
                      </Box>
                    </Box>
                  </Box>

                  {(watch("subsidiariesDetails") || []).map((_, index) => (
                    <Box key={index} {...styles("row")}>
                      <Box {...styles("input")} sx={{ flex: 1 }}>
                        <Input
                          name={`subsidiariesDetails.${index}`}
                          label={`Subsidiary ${index + 1}`}
                          placeholder="Enter subsidiary details"
                          control={control}
                          errors={errors}
                          height="52px"
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          marginTop: "8px",
                        }}
                      >
                        <Button
                          text="Remove"
                          variant="outlined"
                          onClick={() => {
                            const current =
                              getValues("subsidiariesDetails") || [];
                            setValue(
                              "subsidiariesDetails",
                              current.filter((_, i) => i !== index)
                            );
                          }}
                          customStyles={{
                            outlined: { padding: "8px 16px", minWidth: "80px" },
                          }}
                        />
                      </Box>
                    </Box>
                  ))}

                  <Box {...styles("row")}>
                    <Button
                      text="+ Add Subsidiary"
                      variant="outlined"
                      onClick={() => {
                        const current = getValues("subsidiariesDetails") || [];
                        setValue("subsidiariesDetails", [...current, ""]);
                      }}
                      customStyles={{ outlined: { padding: "8px 16px" } }}
                    />
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="cinNumber"
                        label="CIN Number"
                        placeholder="Enter CIN number"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadCertificateOfIncorporation"
                        label="Upload Certificate of Incorporation"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="panNumber"
                        label="PAN Number"
                        placeholder="Enter PAN number"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                            message: "Please enter a valid PAN number",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadPAN"
                        label="Upload PAN"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="gstNumber"
                        label="GST Number"
                        placeholder="Enter GST number"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadGST"
                        label="Upload GST document"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "withHelperText"])}>
                      <Input
                        name="primaryEmail"
                        label="Primary Email ID"
                        placeholder="Enter primary email"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Please enter a valid email address",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="primaryContactNumber"
                        label="Primary Contact Number"
                        placeholder="Enter primary contact number"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^\d{10}$/,
                            message:
                              "Please enter a valid 10-digit phone number",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Typography {...styles("subsectionTitle")}>
                    Office Address
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="officeAddress"
                        label="Address"
                        placeholder="Enter complete office address"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        multiline
                        rows={2}
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Select
                        name="officeState"
                        label="State"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={formattedStateOptions}
                        placeholder="Select state/UT"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Select
                        name="officeDistrict"
                        label="District"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={officeDistrictOptions}
                        placeholder="Select district"
                        disabled={!watch("officeState")}
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="officePincode"
                        label="Pin code"
                        placeholder="Enter 6-digit pin code"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^\d{6}$/,
                            message: "Please enter a valid 6-digit pin code",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>
                </Box>

                {/* B. Authorized Representative Details */}
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    Authorized Representative Details
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="repFullName"
                        label="Full Name"
                        placeholder="Enter full name"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="repDesignation"
                        label="Designation"
                        placeholder="Enter designation"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "withHelperText"])}>
                      <Input
                        name="repEmail"
                        label="Email ID"
                        placeholder="Enter email address"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Please enter a valid email address",
                          },
                        }}
                        errors={errors}
                        height="52px"
                        disabled={!!watch("verifyRepEmail")}
                      />

                      <OTP
                        name="repEmailOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        label="Verify Email"
                        isVerified={!!watch("verifyRepEmail")}
                        type={{
                          type: OtpType.EMAIL,
                          email: watch("repEmail"),
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("repEmailOtp", otp);
                          setValue("verifyRepEmail", true);
                        }}
                        heading="Verify representative email"
                        subHeading="You might have received an OTP on the representative's email. Please enter the same, If not click on resend OTP."
                      />
                    </Box>

                    <Box {...styles(["input", "withHelperText"])}>
                      <PhoneNumber
                        control={control}
                        errors={errors}
                        name="repMobile"
                        label="Mobile Number"
                        disabled={!!watch("verifyRepMobile")}
                      />

                      <OTP
                        name="repMobileOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        isVerified={!!watch("verifyRepMobile")}
                        type={{
                          type: OtpType.MOBILE,
                          phoneNumber: `${watch("repMobile.number")}`,
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("repMobileOtp", otp);
                          setValue("verifyRepMobile", true);
                        }}
                        heading="Verify representative mobile"
                        subHeading="You might have received an OTP on the representative's mobile. Please enter the same, If not click on resend OTP."
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="repEmployeeID"
                        label="Employee ID"
                        placeholder="Enter employee ID"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadRepIDProof"
                        label="Upload Authorized representative ID Proof"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadAuthorizationLetter"
                        label="Upload Authorization Letter"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            {/* NGO - Details */}
            {watch("gcaType") === "NGO" && (
              <>
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    A. NGO Details
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="nameOfNGO"
                        label="Name of NGO*"
                        placeholder="Enter name of NGO"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Select
                        name="registrationType"
                        label="Registration Type*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={registrationTypeOptions}
                        placeholder="Select registration type"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="ngoRegistrationNumber"
                        label="Registration Number*"
                        placeholder="Enter registration number"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadNGORegistrationCertificate"
                        label="Upload registration certificate*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="ngoGSTNumber"
                        label="GST number*"
                        placeholder="Enter GST number"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadNGOGST"
                        label="Upload GST document*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="ngoPANNumber"
                        label="PAN Number*"
                        placeholder="Enter PAN number"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                            message: "Please enter a valid PAN number",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadNGOPAN"
                        label="Upload PAN document*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="status12A80G"
                        label="12A/80G Status (if applicable)"
                        placeholder="Enter 12A/80G status"
                        control={control}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="upload12A80GCertificates"
                        label="Upload 12A/80G Certificates"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        maxLimit={2}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="ngoDarpanID"
                        label="NGO Darpan ID* (if available)"
                        placeholder="Enter NGO Darpan ID"
                        control={control}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadNGODarpanCertificate"
                        label="Upload NGO Darpan certificate (optional)"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "withHelperText"])}>
                      <Input
                        name="ngoEmail"
                        label="Email*"
                        placeholder="Enter email address"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Please enter a valid email address",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="ngoContactNumber"
                        label="Contact Number*"
                        placeholder="Enter contact number"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^\d{10}$/,
                            message:
                              "Please enter a valid 10-digit phone number",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Typography {...styles("subsectionTitle")}>
                    Office Address
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="ngoOfficeAddress"
                        label="Address*"
                        placeholder="Enter complete office address"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        multiline
                        rows={2}
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Select
                        name="ngoOfficeState"
                        label="State*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={formattedStateOptions}
                        placeholder="Select state/UT"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Select
                        name="ngoOfficeDistrict"
                        label="District*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={ngoOfficeDistrictOptions}
                        placeholder="Select district"
                        disabled={!watch("ngoOfficeState")}
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="ngoOfficePincode"
                        label="Pin code*"
                        placeholder="Enter 6-digit pin code"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^\d{6}$/,
                            message: "Please enter a valid 6-digit pin code",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>
                </Box>

                {/* C. Operational Areas */}
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    C. Operational Areas
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="operationActiveStates"
                        label="Operation active in States*"
                        placeholder="e.g., Assam, Uttar Pradesh, Karnataka"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        multiline
                        rows={2}
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Select
                        name="natureOfWork"
                        label="Nature of Work*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={natureOfWorkOptions}
                        placeholder="Select nature of work"
                      />
                    </Box>
                  </Box>
                </Box>

                {/* B. Authorized Representative Details */}
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    B. Authorized Representative Details
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="repFullName"
                        label="Full Name*"
                        placeholder="Enter full name"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="repDesignation"
                        label="Designation*"
                        placeholder="Enter designation"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "withHelperText"])}>
                      <Input
                        name="repEmail"
                        label="Email ID*"
                        placeholder="Enter email address"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Please enter a valid email address",
                          },
                        }}
                        errors={errors}
                        height="52px"
                        disabled={!!watch("verifyRepEmail")}
                      />

                      <OTP
                        name="repEmailOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        label="Verify Email"
                        isVerified={!!watch("verifyRepEmail")}
                        type={{
                          type: OtpType.EMAIL,
                          email: watch("repEmail"),
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("repEmailOtp", otp);
                          setValue("verifyRepEmail", true);
                        }}
                        heading="Verify representative email"
                        subHeading="You might have received an OTP on the representative's email. Please enter the same, If not click on resend OTP."
                      />
                    </Box>

                    <Box {...styles(["input", "withHelperText"])}>
                      <PhoneNumber
                        control={control}
                        errors={errors}
                        name="repMobile"
                        label="Mobile Number*"
                        disabled={!!watch("verifyRepMobile")}
                      />

                      <OTP
                        name="repMobileOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        isVerified={!!watch("verifyRepMobile")}
                        type={{
                          type: OtpType.MOBILE,
                          phoneNumber: `${watch("repMobile.number")}`,
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("repMobileOtp", otp);
                          setValue("verifyRepMobile", true);
                        }}
                        heading="Verify representative mobile"
                        subHeading="You might have received an OTP on the representative's mobile. Please enter the same, If not click on resend OTP."
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="repEmployeeID"
                        label="Employee ID*"
                        placeholder="Enter employee ID"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadRepIDProof"
                        label="Upload Authorized representative ID Proof*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadAuthorizationLetter"
                        label="Upload Authorization Letter*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>
                </Box>

                {/* D. Required Documents */}
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    D. Required Documents
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="turnover"
                        label="Turnover* (in crores)"
                        placeholder="Enter annual turnover"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        type="number"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadAuditedFinancialStatements"
                        label="Audited Financial Statements*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={3}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            {/* Private Company - Business Information */}
            {watch("gcaType") === "PRIVATE_COMPANY" && (
              <>
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    A. Business Information
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="companyName"
                        label="Company Name*"
                        placeholder="Enter company name"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="cinNumber"
                        label="CIN*"
                        placeholder="Enter CIN"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadCertificateOfIncorporationPC"
                        label="Upload Certificate of Incorporation*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="companyPAN"
                        label="PAN*"
                        placeholder="Enter PAN"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                            message: "Please enter a valid PAN number",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadPANPC"
                        label="Upload PAN document*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="companyGSTNumber"
                        label="GST Number*"
                        placeholder="Enter GST number"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadGSTCertificatePC"
                        label="Upload GST certificate*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="parentCompanyDetailsPC"
                        label="Parent Company details (if any)"
                        placeholder="Enter parent company details"
                        control={control}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Typography {...styles("subsectionTitle")}>
                        Subsidiaries details
                      </Typography>
                      <Box {...styles("fieldHelperText")}>
                        Add multiple subsidiaries if applicable
                      </Box>
                    </Box>
                  </Box>

                  {(watch("subsidiariesDetailsPC") || []).map((_, index) => (
                    <Box key={index} {...styles("row")}>
                      <Box {...styles("input")} sx={{ flex: 1 }}>
                        <Input
                          name={`subsidiariesDetailsPC.${index}`}
                          label={`Subsidiary ${index + 1}`}
                          placeholder="Enter subsidiary details"
                          control={control}
                          errors={errors}
                          height="52px"
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          marginTop: "8px",
                        }}
                      >
                        <Button
                          text="Remove"
                          variant="outlined"
                          onClick={() => {
                            const current =
                              getValues("subsidiariesDetailsPC") || [];
                            setValue(
                              "subsidiariesDetailsPC",
                              current.filter((_, i) => i !== index)
                            );
                          }}
                          customStyles={{
                            outlined: { padding: "8px 16px", minWidth: "80px" },
                          }}
                        />
                      </Box>
                    </Box>
                  ))}

                  <Box {...styles("row")}>
                    <Button
                      text="+ Add Subsidiary"
                      variant="outlined"
                      onClick={() => {
                        const current =
                          getValues("subsidiariesDetailsPC") || [];
                        setValue("subsidiariesDetailsPC", [...current, ""]);
                      }}
                      customStyles={{ outlined: { padding: "8px 16px" } }}
                    />
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="natureOfBusinessPC"
                        label="Nature of Business*"
                        placeholder="Enter nature of business"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="statesOfOperationPC"
                        label="States of Operation*"
                        placeholder="Ex. Uttar Pradesh, Assam, Karnataka"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        multiline
                        rows={2}
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="incorporationDate"
                        label="Incorporation Date*"
                        placeholder="Select date"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        type="date"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "withHelperText"])}>
                      <Input
                        name="companyEmail"
                        label="Email*"
                        placeholder="Enter company email"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Please enter a valid email address",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "withHelperText"])}>
                      <Input
                        name="companyMobileNumber"
                        label="Mobile number*"
                        placeholder="Enter mobile number"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^\d{10}$/,
                            message:
                              "Please enter a valid 10-digit phone number",
                          },
                        }}
                        errors={errors}
                        height="52px"
                        disabled={!!watch("verifyCompanyMobile")}
                      />

                      <OTP
                        name="companyMobileOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        isVerified={!!watch("verifyCompanyMobile")}
                        type={{
                          type: OtpType.MOBILE,
                          phoneNumber: watch("companyMobileNumber"),
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("companyMobileOtp", otp);
                          setValue("verifyCompanyMobile", true);
                        }}
                        heading="Verify company mobile number"
                        subHeading="You might have received an OTP on your company mobile number. Please enter the same, If not click on resend OTP."
                      />
                    </Box>
                  </Box>

                  <Typography {...styles("subsectionTitle")}>
                    Office Address
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="pcOfficeAddress"
                        label="Address*"
                        placeholder="Enter complete office address"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        multiline
                        rows={2}
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Select
                        name="pcOfficeState"
                        label="State*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={formattedStateOptions}
                        placeholder="Select state/UT"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Select
                        name="pcOfficeDistrict"
                        label="District*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={pcOfficeDistrictOptions}
                        placeholder="Select district"
                        disabled={!watch("pcOfficeState")}
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="pcOfficePincode"
                        label="Pin code*"
                        placeholder="Enter 6-digit pin code"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^\d{6}$/,
                            message: "Please enter a valid 6-digit pin code",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>
                </Box>

                {/* B. Authorized Representative Details */}
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    B. Authorized Representative Details
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="repFullName"
                        label="Full Name*"
                        placeholder="Enter full name"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="repDesignation"
                        label="Designation*"
                        placeholder="Enter designation"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "withHelperText"])}>
                      <Input
                        name="repEmail"
                        label="Email ID*"
                        placeholder="Enter email address"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Please enter a valid email address",
                          },
                        }}
                        errors={errors}
                        height="52px"
                        disabled={!!watch("verifyRepEmail")}
                      />

                      <OTP
                        name="repEmailOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        label="Verify Email"
                        isVerified={!!watch("verifyRepEmail")}
                        type={{
                          type: OtpType.EMAIL,
                          email: watch("repEmail"),
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("repEmailOtp", otp);
                          setValue("verifyRepEmail", true);
                        }}
                        heading="Verify representative email"
                        subHeading="You might have received an OTP on the representative's email. Please enter the same, If not click on resend OTP."
                      />
                    </Box>

                    <Box {...styles(["input", "withHelperText"])}>
                      <PhoneNumber
                        control={control}
                        errors={errors}
                        name="repMobile"
                        label="Mobile Number*"
                        disabled={!!watch("verifyRepMobile")}
                      />

                      <OTP
                        name="repMobileOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        isVerified={!!watch("verifyRepMobile")}
                        type={{
                          type: OtpType.MOBILE,
                          phoneNumber: `${watch("repMobile.number")}`,
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("repMobileOtp", otp);
                          setValue("verifyRepMobile", true);
                        }}
                        heading="Verify representative mobile"
                        subHeading="You might have received an OTP on the representative's mobile. Please enter the same, If not click on resend OTP."
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="repEmployeeID"
                        label="Employee ID*"
                        placeholder="Enter employee ID"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadRepIDProof"
                        label="Upload Authorized representative ID Proof*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadAuthorizationLetter"
                        label="Upload Authorization Letter*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>
                </Box>

                {/* C. Required Documents */}
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    C. Required Documents
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="turnover"
                        label="Turnover*"
                        placeholder="Enter turnover in crores"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        type="number"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadAuditedFinancialStatementsPC"
                        label="Audited Financial Statements*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={3}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            {/* Public Limited Company - Company Details */}
            {watch("gcaType") === "PUBLIC_LIMITED_COMPANY" && (
              <>
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    A. Company Details
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="registeredCompanyName"
                        label="Registered Company Name*"
                        placeholder="Enter registered company name"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="plcCINNumber"
                        label="CIN*"
                        placeholder="Enter CIN"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadIncorporationDocumentsPLC"
                        label="Upload Incorporation Documents*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="plcPANNumber"
                        label="PAN No*"
                        placeholder="Enter PAN"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                            message: "Please enter a valid PAN number",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadPANPLC"
                        label="Upload PAN*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="plcGSTNumber"
                        label="GST No.*"
                        placeholder="Enter GST number"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadGSTPLC"
                        label="Upload PAN, GST*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={2}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Select
                        name="sebiListingStatus"
                        label="SEBI Listing Status"
                        control={control}
                        errors={errors}
                        height="52px"
                        options={sebiListingStatusOptions}
                        placeholder="Select SEBI listing status"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="parentCompanyDetailsPLC"
                        label="Parent Company details (if any)"
                        placeholder="Enter parent company details"
                        control={control}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Typography {...styles("subsectionTitle")}>
                        Subsidiaries details
                      </Typography>
                      <Box {...styles("fieldHelperText")}>
                        Add multiple subsidiaries if applicable
                      </Box>
                    </Box>
                  </Box>

                  {(watch("subsidiariesDetailsPLC") || []).map((_, index) => (
                    <Box key={index} {...styles("row")}>
                      <Box {...styles("input")} sx={{ flex: 1 }}>
                        <Input
                          name={`subsidiariesDetailsPLC.${index}`}
                          label={`Subsidiary ${index + 1}`}
                          placeholder="Enter subsidiary details"
                          control={control}
                          errors={errors}
                          height="52px"
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          marginTop: "8px",
                        }}
                      >
                        <Button
                          text="Remove"
                          variant="outlined"
                          onClick={() => {
                            const current =
                              getValues("subsidiariesDetailsPLC") || [];
                            setValue(
                              "subsidiariesDetailsPLC",
                              current.filter((_, i) => i !== index)
                            );
                          }}
                          customStyles={{
                            outlined: { padding: "8px 16px", minWidth: "80px" },
                          }}
                        />
                      </Box>
                    </Box>
                  ))}

                  <Box {...styles("row")}>
                    <Button
                      text="+ Add Subsidiary"
                      variant="outlined"
                      onClick={() => {
                        const current =
                          getValues("subsidiariesDetailsPLC") || [];
                        setValue("subsidiariesDetailsPLC", [...current, ""]);
                      }}
                      customStyles={{ outlined: { padding: "8px 16px" } }}
                    />
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "withHelperText"])}>
                      <Input
                        name="corporateOfficeEmail"
                        label="Corporate Office Email*"
                        placeholder="Enter corporate office email"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Please enter a valid email address",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="contactNumberPLC"
                        label="Contact No.*"
                        placeholder="Enter contact number"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^\d{10}$/,
                            message:
                              "Please enter a valid 10-digit phone number",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="registeredOffice"
                        label="Registered Office*"
                        placeholder="Enter registered office address"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        multiline
                        rows={2}
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="corporateOffice"
                        label="Corporate Office*"
                        placeholder="Enter corporate office address"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        multiline
                        rows={2}
                      />
                    </Box>
                  </Box>

                  <Typography {...styles("subsectionTitle")}>
                    Office Address
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="plcOfficeAddress"
                        label="Address*"
                        placeholder="Enter complete office address"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        multiline
                        rows={2}
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Select
                        name="plcOfficeState"
                        label="State*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={formattedStateOptions}
                        placeholder="Select state/UT"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Select
                        name="plcOfficeDistrict"
                        label="District*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={plcOfficeDistrictOptions}
                        placeholder="Select district"
                        disabled={!watch("plcOfficeState")}
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="plcOfficePincode"
                        label="Pin code*"
                        placeholder="Enter 6-digit pin code"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^\d{6}$/,
                            message: "Please enter a valid 6-digit pin code",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>
                </Box>

                {/* B. Authorized Representative Details */}
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    B. Authorized Representative Details
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="repFullName"
                        label="Full Name*"
                        placeholder="Enter full name"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="repDesignation"
                        label="Designation*"
                        placeholder="Enter designation"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "withHelperText"])}>
                      <Input
                        name="repEmail"
                        label="Email ID*"
                        placeholder="Enter email address"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Please enter a valid email address",
                          },
                        }}
                        errors={errors}
                        height="52px"
                        disabled={!!watch("verifyRepEmail")}
                      />

                      <OTP
                        name="repEmailOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        label="Verify Email"
                        isVerified={!!watch("verifyRepEmail")}
                        type={{
                          type: OtpType.EMAIL,
                          email: watch("repEmail"),
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("repEmailOtp", otp);
                          setValue("verifyRepEmail", true);
                        }}
                        heading="Verify representative email"
                        subHeading="You might have received an OTP on the representative's email. Please enter the same, If not click on resend OTP."
                      />
                    </Box>

                    <Box {...styles(["input", "withHelperText"])}>
                      <PhoneNumber
                        control={control}
                        errors={errors}
                        name="repMobile"
                        label="Mobile Number*"
                        disabled={!!watch("verifyRepMobile")}
                      />

                      <OTP
                        name="repMobileOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        isVerified={!!watch("verifyRepMobile")}
                        type={{
                          type: OtpType.MOBILE,
                          phoneNumber: `${watch("repMobile.number")}`,
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("repMobileOtp", otp);
                          setValue("verifyRepMobile", true);
                        }}
                        heading="Verify representative mobile"
                        subHeading="You might have received an OTP on the representative's mobile. Please enter the same, If not click on resend OTP."
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="repEmployeeID"
                        label="Employee ID*"
                        placeholder="Enter employee ID"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadRepIDProof"
                        label="Upload Authorized representative ID Proof*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadAuthorizationLetter"
                        label="Upload Authorization Letter*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>
                </Box>

                {/* C. Required Documents */}
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    C. Required Documents
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="turnover"
                        label="Turnover*"
                        placeholder="Enter turnover in crores"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        type="number"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadAuditedFinancialStatementsPLC"
                        label="Audited Financial Statements*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={3}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            {/* Philanthropies / Foundations - Organization Details */}
            {watch("gcaType") === "PHILANTHROPY" && (
              <>
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    A. Organization Details
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="foundationName"
                        label="Foundation Name*"
                        placeholder="Enter foundation name"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Select
                        name="legalStatusFoundation"
                        label="Legal Status*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={legalStatusFoundationOptions}
                        placeholder="Select legal status"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="foundationRegistrationNumber"
                        label="Registration Number*"
                        placeholder="Enter registration number"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadRegistrationCertificateFoundation"
                        label="Upload Registration Certificate*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="foundationPANNumber"
                        label="PAN*"
                        placeholder="Enter PAN"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                            message: "Please enter a valid PAN number",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadPANFoundation"
                        label="Upload PAN*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="fundingSource"
                        label="Funding Source*"
                        placeholder="Enter funding source"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "withHelperText"])}>
                      <Input
                        name="foundationEmail"
                        label="Email*"
                        placeholder="Enter foundation email"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Please enter a valid email address",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "withHelperText"])}>
                      <PhoneNumber
                        control={control}
                        errors={errors}
                        name="foundationMobile"
                        label="Mobile Number*"
                        disabled={!!watch("verifyFoundationMobile")}
                      />

                      <OTP
                        name="foundationMobileOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        isVerified={!!watch("verifyFoundationMobile")}
                        type={{
                          type: OtpType.MOBILE,
                          phoneNumber: `${watch("foundationMobile.number")}`,
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("foundationMobileOtp", otp);
                          setValue("verifyFoundationMobile", true);
                        }}
                        heading="Verify foundation mobile"
                        subHeading="You might have received an OTP on the foundation's mobile. Please enter the same, If not click on resend OTP."
                      />
                    </Box>
                  </Box>

                  <Typography {...styles("subsectionTitle")}>
                    Office Address
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="foundationOfficeAddress"
                        label="Address*"
                        placeholder="Enter complete office address"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        multiline
                        rows={2}
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Select
                        name="foundationOfficeState"
                        label="State*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={formattedStateOptions}
                        placeholder="Select state/UT"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Select
                        name="foundationOfficeDistrict"
                        label="District*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={foundationOfficeDistrictOptions}
                        placeholder="Select district"
                        disabled={!watch("foundationOfficeState")}
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="foundationOfficePincode"
                        label="Pin code*"
                        placeholder="Enter 6-digit pin code"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^\d{6}$/,
                            message: "Please enter a valid 6-digit pin code",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>
                </Box>

                {/* B. Authorized Representative Details */}
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    B. Authorized Representative Details
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="repFullName"
                        label="Full Name*"
                        placeholder="Enter full name"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="repDesignation"
                        label="Designation*"
                        placeholder="Enter designation"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "withHelperText"])}>
                      <Input
                        name="repEmail"
                        label="Email ID*"
                        placeholder="Enter email address"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Please enter a valid email address",
                          },
                        }}
                        errors={errors}
                        height="52px"
                        disabled={!!watch("verifyRepEmail")}
                      />

                      <OTP
                        name="repEmailOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        label="Verify Email"
                        isVerified={!!watch("verifyRepEmail")}
                        type={{
                          type: OtpType.EMAIL,
                          email: watch("repEmail"),
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("repEmailOtp", otp);
                          setValue("verifyRepEmail", true);
                        }}
                        heading="Verify representative email"
                        subHeading="You might have received an OTP on the representative's email. Please enter the same, If not click on resend OTP."
                      />
                    </Box>

                    <Box {...styles(["input", "withHelperText"])}>
                      <PhoneNumber
                        control={control}
                        errors={errors}
                        name="repMobile"
                        label="Mobile Number*"
                        disabled={!!watch("verifyRepMobile")}
                      />

                      <OTP
                        name="repMobileOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        isVerified={!!watch("verifyRepMobile")}
                        type={{
                          type: OtpType.MOBILE,
                          phoneNumber: `${watch("repMobile.number")}`,
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("repMobileOtp", otp);
                          setValue("verifyRepMobile", true);
                        }}
                        heading="Verify representative mobile"
                        subHeading="You might have received an OTP on the representative's mobile. Please enter the same, If not click on resend OTP."
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="repEmployeeID"
                        label="Employee ID*"
                        placeholder="Enter employee ID"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadRepIDProof"
                        label="Upload Authorized representative ID Proof*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadAuthorizationLetter"
                        label="Upload Authorization Letter*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>
                </Box>

                {/* C. Required Documents */}
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    C. Required Documents
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="turnover"
                        label="Turnover*"
                        placeholder="Enter turnover in crores"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        type="number"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadAuditedFinancialStatementsFoundation"
                        label="Audited Financial Statements*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={3}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="csrEsgDetails"
                        label="CSR and ESG details (if any)"
                        placeholder="Enter CSR and ESG details"
                        control={control}
                        errors={errors}
                        height="52px"
                        multiline
                        rows={2}
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadCSREsgDocument"
                        label="Upload CSR and ESG document"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        maxLimit={3}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            {/* Individuals - Personal Details */}
            {watch("gcaType") === "INDIVIDUAL" && (
              <>
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    A. Personal Details
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="individualFullName"
                        label="Full Name*"
                        placeholder="Enter full name"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadPassportPhoto"
                        label="Upload Passport-size Photograph"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="individualDateOfBirth"
                        label="Date of Birth"
                        placeholder="Select date"
                        control={control}
                        errors={errors}
                        height="52px"
                        type="date"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Select
                        name="individualGender"
                        label="Gender"
                        control={control}
                        errors={errors}
                        height="52px"
                        options={genderOptions}
                        placeholder="Select gender"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="individualOccupation"
                        label="Occupation*"
                        placeholder="Enter occupation"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="individualAadhaarNumber"
                        label="Aadhar Number*"
                        placeholder="Enter 12-digit Aadhar number"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^\d{12}$/,
                            message:
                              "Please enter a valid 12-digit Aadhar number",
                          },
                        }}
                        errors={errors}
                        height="52px"
                        type="number"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadIndividualAadhaar"
                        label="Upload Aadhar card*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="individualPANNumber"
                        label="PAN Number*"
                        placeholder="Enter PAN"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                            message: "Please enter a valid PAN number",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadIndividualPAN"
                        label="Upload PAN*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "withHelperText"])}>
                      <Input
                        name="individualEmail"
                        label="Email*"
                        placeholder="Enter email address"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Please enter a valid email address",
                          },
                        }}
                        errors={errors}
                        height="52px"
                        disabled={!!watch("verifyIndividualEmail")}
                      />

                      <OTP
                        name="individualEmailOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        label="Verify Email"
                        isVerified={!!watch("verifyIndividualEmail")}
                        type={{
                          type: OtpType.EMAIL,
                          email: watch("individualEmail"),
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("individualEmailOtp", otp);
                          setValue("verifyIndividualEmail", true);
                        }}
                        heading="Verify email"
                        subHeading="You might have received an OTP on your email. Please enter the same, If not click on resend OTP."
                      />
                    </Box>

                    <Box {...styles(["input", "withHelperText"])}>
                      <PhoneNumber
                        control={control}
                        errors={errors}
                        name="individualMobile"
                        label="Mobile Number*"
                        disabled={!!watch("verifyIndividualMobile")}
                      />

                      <OTP
                        name="individualMobileOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        isVerified={!!watch("verifyIndividualMobile")}
                        type={{
                          type: OtpType.MOBILE,
                          phoneNumber: `${watch("individualMobile.number")}`,
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("individualMobileOtp", otp);
                          setValue("verifyIndividualMobile", true);
                        }}
                        heading="Verify mobile number"
                        subHeading="You might have received an OTP on your mobile. Please enter the same, If not click on resend OTP."
                      />
                    </Box>
                  </Box>

                  <Typography {...styles("subsectionTitle")}>
                    Address
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="individualAddress"
                        label="Address*"
                        placeholder="Enter complete address"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        multiline
                        rows={2}
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Select
                        name="individualState"
                        label="State*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={formattedStateOptions}
                        placeholder="Select state/UT"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Select
                        name="individualDistrict"
                        label="District*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={individualDistrictOptions}
                        placeholder="Select district"
                        disabled={!watch("individualState")}
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="individualPincode"
                        label="Pin code*"
                        placeholder="Enter 6-digit pin code"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^\d{6}$/,
                            message: "Please enter a valid 6-digit pin code",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>
                </Box>

                {/* B. Occupation Details */}
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    B. Occupation Details
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Select
                        name="individualOccupationDetails"
                        label="Occupation Details*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={occupationDetailsOptions}
                        placeholder="Select occupation category"
                      />
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            {/* Registered Group of Individuals - Group Details */}
            {watch("gcaType") === "REGISTERED_GROUP" && (
              <>
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    A. Group Details
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="groupName"
                        label="Group Name*"
                        placeholder="Enter group name"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Select
                        name="groupType"
                        label="Type*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={groupTypeOptions}
                        placeholder="Select group type"
                      />
                    </Box>
                  </Box>

                  {watch("groupType") === "OTHER" && (
                    <Box {...styles("row")}>
                      <Box {...styles("input")}>
                        <Input
                          name="groupTypeOther"
                          label="Specify Other Group Type*"
                          placeholder="Enter group type"
                          control={control}
                          rules={{ required: errorMessage.required }}
                          errors={errors}
                          height="52px"
                        />
                      </Box>
                    </Box>
                  )}

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "withHelperText"])}>
                      <Input
                        name="groupEmail"
                        label="Email*"
                        placeholder="Enter group email"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Please enter a valid email address",
                          },
                        }}
                        errors={errors}
                        height="52px"
                        disabled={!!watch("verifyGroupEmail")}
                      />

                      <OTP
                        name="groupEmailOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        label="Verify Email"
                        isVerified={!!watch("verifyGroupEmail")}
                        type={{
                          type: OtpType.EMAIL,
                          email: watch("groupEmail"),
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("groupEmailOtp", otp);
                          setValue("verifyGroupEmail", true);
                        }}
                        heading="Verify group email"
                        subHeading="You might have received an OTP on your group email. Please enter the same, If not click on resend OTP."
                      />
                    </Box>

                    <Box {...styles(["input", "withHelperText"])}>
                      <PhoneNumber
                        control={control}
                        errors={errors}
                        name="groupContactNumber"
                        label="Contact Number*"
                        disabled={!!watch("verifyGroupContact")}
                      />

                      <OTP
                        name="groupContactOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        isVerified={!!watch("verifyGroupContact")}
                        type={{
                          type: OtpType.MOBILE,
                          phoneNumber: `${watch("groupContactNumber.number")}`,
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("groupContactOtp", otp);
                          setValue("verifyGroupContact", true);
                        }}
                        heading="Verify contact number"
                        subHeading="You might have received an OTP on the group contact number. Please enter the same, If not click on resend OTP."
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="groupRegistrationNumber"
                        label="Registration Number*"
                        placeholder="Enter registration number"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadGroupRegistrationCertificate"
                        label="Upload Group Registration Certificate*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>

                  <Typography {...styles("subsectionTitle")}>
                    Office Address
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="groupOfficeAddress"
                        label="Address*"
                        placeholder="Enter complete office address"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                        multiline
                        rows={2}
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Select
                        name="groupOfficeState"
                        label="State*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={formattedStateOptions}
                        placeholder="Select state/UT"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Select
                        name="groupOfficeDistrict"
                        label="District*"
                        control={control}
                        errors={errors}
                        height="52px"
                        rules={{ required: errorMessage.required }}
                        options={groupOfficeDistrictOptions}
                        placeholder="Select district"
                        disabled={!watch("groupOfficeState")}
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="groupOfficePincode"
                        label="Pin code*"
                        placeholder="Enter 6-digit pin code"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^\d{6}$/,
                            message: "Please enter a valid 6-digit pin code",
                          },
                        }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>
                </Box>

                {/* B. Authorized Representative Details */}
                <Box {...styles("section")}>
                  <Typography {...styles("sectionTitle")}>
                    B. Authorized Representative Details
                  </Typography>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="repFullName"
                        label="Full Name*"
                        placeholder="Enter full name"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>

                    <Box {...styles("input")}>
                      <Input
                        name="repDesignation"
                        label="Designation*"
                        placeholder="Enter designation"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "withHelperText"])}>
                      <Input
                        name="repEmail"
                        label="Email ID*"
                        placeholder="Enter email address"
                        control={control}
                        rules={{
                          required: errorMessage.required,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Please enter a valid email address",
                          },
                        }}
                        errors={errors}
                        height="52px"
                        disabled={!!watch("verifyRepEmail")}
                      />

                      <OTP
                        name="repEmailOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        label="Verify Email"
                        isVerified={!!watch("verifyRepEmail")}
                        type={{
                          type: OtpType.EMAIL,
                          email: watch("repEmail"),
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("repEmailOtp", otp);
                          setValue("verifyRepEmail", true);
                        }}
                        heading="Verify representative email"
                        subHeading="You might have received an OTP on the representative's email. Please enter the same, If not click on resend OTP."
                      />
                    </Box>

                    <Box {...styles(["input", "withHelperText"])}>
                      <PhoneNumber
                        control={control}
                        errors={errors}
                        name="repMobile"
                        label="Mobile Number*"
                        disabled={!!watch("verifyRepMobile")}
                      />

                      <OTP
                        name="repMobileOtp"
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                        errors={errors}
                        watch={watch}
                        isVerified={!!watch("verifyRepMobile")}
                        type={{
                          type: OtpType.MOBILE,
                          phoneNumber: `${watch("repMobile.number")}`,
                        }}
                        verifyOtp={(otp: string) => {
                          setValue("repMobileOtp", otp);
                          setValue("verifyRepMobile", true);
                        }}
                        heading="Verify representative mobile"
                        subHeading="You might have received an OTP on the representative's mobile. Please enter the same, If not click on resend OTP."
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles("input")}>
                      <Input
                        name="repEmployeeID"
                        label="Employee ID*"
                        placeholder="Enter employee ID"
                        control={control}
                        rules={{ required: errorMessage.required }}
                        errors={errors}
                        height="52px"
                      />
                    </Box>
                  </Box>

                  <Box {...styles("row")}>
                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadRepIDProof"
                        label="Upload Authorized representative ID Proof*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>

                    <Box {...styles(["input", "fileUpload"])}>
                      <FileUpload
                        name="uploadAuthorizationLetter"
                        label="Upload Authorization Letter*"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                        rules={{ required: errorMessage.required }}
                        maxLimit={1}
                        accept="image/jpeg,image/jpg,.pdf"
                      />
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            {/* Account Security Section - Show for all types */}
            {watch("gcaType") && (
              <Box {...styles("section")}>
                <Typography {...styles("sectionTitle")}>
                  Account Security
                </Typography>

                <Box {...styles("row")}>
                  <Box {...styles("input")}>
                    <Password
                      name="password"
                      label="Password"
                      placeholder="Enter your password"
                      control={control}
                      getValues={getValues}
                      showHelperText
                      height="52px"
                      rules={{
                        required: true,
                        validate: {
                          isLength: (value: string) =>
                            value && value.length >= 8,
                          isUpper: (value) =>
                            value &&
                            passwordRegex.isUpper.test(value.toString()),
                          isLower: (value) =>
                            value &&
                            passwordRegex.isLower.test(value.toString()),
                          isNum: (value) =>
                            value && passwordRegex.isNum.test(value.toString()),
                          hasSpecialCharacter: (value) =>
                            value &&
                            passwordRegex.hasSpecialCharacter.test(
                              value.toString()
                            ),
                        },
                      }}
                      errors={errors}
                    />
                  </Box>

                  <Box {...styles("input")}>
                    <Password
                      name="confirmPassword"
                      label="Confirm Password"
                      placeholder="Re-enter your password"
                      control={control}
                      getValues={getValues}
                      height="52px"
                      rules={{
                        required: errorMessage.required,
                        validate: (value) =>
                          value === getValues("password") ||
                          "Passwords do not match",
                      }}
                      errors={errors}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {/* Terms and Conditions - Show for all types */}
            {watch("gcaType") && (
              <Box {...styles("termsSection")}>
                <Box {...styles("termsCheckbox")}>
                  <input
                    type="checkbox"
                    onChange={(e) => setValue("acceptTerms", e.target.checked)}
                  />
                  <Typography {...styles("termsText")}>
                    I agree to the{" "}
                    <span
                      {...styles("termsLink")}
                      onClick={() => navigate(path.privacyPolicy)}
                    >
                      Terms and Conditions
                    </span>{" "}
                    and{" "}
                    <span
                      {...styles("termsLink")}
                      onClick={() => navigate(path.privacyPolicy)}
                    >
                      Privacy Policy
                    </span>
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Submit Button - Show for all types */}
            {watch("gcaType") && (
              <Box {...styles("buttonContainer")}>
                <Button
                  text="Back to Login"
                  variant="outlined"
                  onClick={() => navigate(path.login)}
                  customStyles={{ outlined: defaultStyles.backButton }}
                />
                <Button
                  text={loading ? "Submitting..." : "Submit Registration"}
                  type="submit"
                  disabled={!isValid || loading}
                  customStyles={{ contained: defaultStyles.submitButton }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Success Modal */}
      <SuccessModal
        open={openModal.open}
        heading={openModal.heading}
        subheading={openModal.subheading}
        description={openModal.description}
        btnText={openModal.btnText}
        handleModalClose={handleModalClose}
        onClick={handleModalClose}
        type={openModal.type}
      />

      {/* Snackbar */}
      <Snackbar
        open={!!showSnackbar.message}
        message={showSnackbar.message}
        severity={showSnackbar.severity}
        resetSnackbar={() =>
          setShowSnackbar({ severity: "success", message: "" })
        }
      />
    </>
  );
};

export default GCARegistration;
